import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  Plus,
  Search,
  FileText,
  Book,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Circle,
  ArrowRight,
  Trash2,
  Play,
  BookOpen,
  GraduationCap,
  Target,
  Rocket
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LearningPaths from '../components/LearningPaths';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['EHR', 'RCM', 'Practice Management', 'CCM', 'RPM', 'APCM', 'AWV', 'Brand', 'Compliance', 'Sales'];

// Category icons and colors
const CATEGORY_CONFIG = {
  'EHR': { icon: FileText, color: 'bg-blue-100 text-blue-700', description: 'Electronic Health Records system guidelines' },
  'RCM': { icon: Target, color: 'bg-green-100 text-green-700', description: 'Revenue Cycle Management best practices' },
  'Practice Management': { icon: BookOpen, color: 'bg-purple-100 text-purple-700', description: 'Practice management solutions' },
  'CCM': { icon: GraduationCap, color: 'bg-amber-100 text-amber-700', description: 'Chronic Care Management program' },
  'RPM': { icon: GraduationCap, color: 'bg-rose-100 text-rose-700', description: 'Remote Patient Monitoring guidelines' },
  'APCM': { icon: GraduationCap, color: 'bg-cyan-100 text-cyan-700', description: 'Advanced Primary Care Management' },
  'AWV': { icon: GraduationCap, color: 'bg-indigo-100 text-indigo-700', description: 'Annual Wellness Visit program' },
  'Brand': { icon: Book, color: 'bg-slate-100 text-slate-700', description: 'Brand guidelines and messaging' },
  'Compliance': { icon: FileText, color: 'bg-red-100 text-red-700', description: 'Compliance requirements' },
  'Sales': { icon: Target, color: 'bg-emerald-100 text-emerald-700', description: 'Sales techniques and scripts' },
};

// Markdown components for well-formatted rendering
const mdComponents = {
  h3: ({ children }) => <h3 className="text-lg font-bold text-[#1B365D] mt-8 mb-3">{children}</h3>,
  p: ({ children }) => <p className="text-[15px] leading-relaxed text-slate-700 mb-4">{children}</p>,
  ul: ({ children }) => <ul className="space-y-2 mb-5 ml-1">{children}</ul>,
  ol: ({ children }) => <ol className="space-y-2 mb-5 ml-1 list-decimal list-inside">{children}</ol>,
  li: ({ children }) => (
    <li className="text-[15px] leading-relaxed text-slate-700 flex items-start gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-[#1B365D]/40 mt-2.5 flex-shrink-0" />
      <span className="flex-1">{children}</span>
    </li>
  ),
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#0071E3] hover:underline font-medium">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-5 rounded-xl border border-slate-200">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#1B365D]/5 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => <th className="px-4 py-3 text-left text-xs font-semibold text-[#1B365D] uppercase tracking-wider">{children}</th>,
  td: ({ children }) => <td className="px-4 py-3 text-sm text-slate-700 border-b border-slate-100">{children}</td>,
  tr: ({ children }) => <tr className="hover:bg-slate-50/50 transition-colors">{children}</tr>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-3 border-[#0071E3] bg-blue-50/50 px-4 py-3 my-4 rounded-r-lg text-slate-700">
      {children}
    </blockquote>
  ),
  em: ({ children }) => <em className="text-slate-500 not-italic text-sm">{children}</em>,
  hr: () => <hr className="my-8 border-slate-200" />,
};

// Step-by-step viewer component
const StepByStepViewer = ({ guideline, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Parse content into steps by ## headers
  const parseSteps = (content) => {
    if (!content) return [{ title: 'Introduction', content: '' }];
    
    const sections = content.split(/(?=^## )/m).filter(s => s.trim());
    
    if (sections.length === 0) {
      return [{ title: 'Overview', content: content }];
    }
    
    return sections.map((section, idx) => {
      const lines = section.trim().split('\n');
      const firstLine = lines[0].replace(/^##\s*/, '').trim();
      const restContent = lines.slice(1).join('\n').trim();
      
      return {
        title: firstLine || `Step ${idx + 1}`,
        content: restContent || section
      };
    });
  };
  
  const steps = parseSteps(guideline.content);
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="border-b border-slate-200 px-6 py-3 bg-white">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onClose} className="text-slate-600 hover:text-slate-900 gap-2">
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="h-5 w-px bg-slate-200" />
            <h1 className="font-semibold text-[#1B365D] text-sm md:text-base">{guideline.title}</h1>
          </div>
          <Badge className="bg-amber-100 text-amber-700 border-0">{guideline.category}</Badge>
        </div>
      </div>
      
      {/* Progress */}
      <div className="px-6 py-2.5 bg-slate-50/80 border-b border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-slate-500 font-medium">Step {currentStep + 1} of {steps.length}</span>
            <span className="text-xs font-semibold text-[#1B365D]">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>
      </div>
      
      {/* Content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-72 bg-slate-50/50 border-r border-slate-200 overflow-y-auto p-4 hidden md:block">
          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-2">Contents</h3>
          <div className="space-y-0.5">
            {steps.map((step, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-[13px] transition-all ${
                  idx === currentStep 
                    ? 'bg-white text-[#1B365D] font-medium shadow-sm border border-slate-200' 
                    : idx < currentStep 
                      ? 'text-emerald-600 hover:bg-white/80' 
                      : 'text-slate-500 hover:bg-white/80 hover:text-slate-700'
                }`}
              >
                {idx < currentStep ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                ) : idx === currentStep ? (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-[#1B365D] flex-shrink-0" />
                ) : (
                  <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                )}
                <span className="truncate">{step.title}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-8">
                  <span className="text-xs font-semibold text-[#0071E3] uppercase tracking-wider">Step {currentStep + 1}</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1B365D] mt-1.5 leading-tight">{steps[currentStep].title}</h2>
                  <div className="w-12 h-0.5 bg-[#0071E3] mt-4 rounded-full" />
                </div>
                
                <div className="ccm-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {steps[currentStep].content}
                  </ReactMarkdown>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-200 px-6 py-3 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="gap-2 rounded-full"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="hidden sm:flex items-center gap-1.5">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep ? 'bg-[#1B365D] scale-125' : idx < currentStep ? 'bg-emerald-400' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
          
          <Button
            onClick={handleNext}
            className="gap-2 bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full"
          >
            {currentStep === steps.length - 1 ? (
              <>
                Complete
                <CheckCircle2 className="w-4 h-4" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Guidelines = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const [guidelines, setGuidelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [viewingGuideline, setViewingGuideline] = useState(null);
  const [activeTab, setActiveTab] = useState('learning-paths');
  const [completedGuidelines, setCompletedGuidelines] = useState(() => {
    const saved = localStorage.getItem('completedGuidelines');
    return saved ? JSON.parse(saved) : [];
  });
  const [newGuideline, setNewGuideline] = useState({
    title: '',
    content: '',
    category: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchGuidelines();
  }, []);

  useEffect(() => {
    localStorage.setItem('completedGuidelines', JSON.stringify(completedGuidelines));
  }, [completedGuidelines]);

  const fetchGuidelines = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guidelines`, getAuthHeaders());
      setGuidelines(response.data);
    } catch (error) {
      toast.error('Failed to load guidelines');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGuideline = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/guidelines`, newGuideline, getAuthHeaders());
      toast.success('Guideline created');
      setShowCreateModal(false);
      setNewGuideline({ title: '', content: '', category: '', tags: [] });
      fetchGuidelines();
    } catch (error) {
      toast.error('Failed to create guideline');
    }
  };

  const handleDeleteGuideline = async (id) => {
    if (!window.confirm('Are you sure you want to delete this guideline?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/guidelines/${id}`, getAuthHeaders());
      toast.success('Guideline deleted');
      setSelectedGuideline(null);
      fetchGuidelines();
    } catch (error) {
      toast.error('Failed to delete guideline');
    }
  };

  const handleCompleteGuideline = (guidelineId) => {
    if (!completedGuidelines.includes(guidelineId)) {
      setCompletedGuidelines([...completedGuidelines, guidelineId]);
      toast.success('Guideline completed! Great job!');
    }
    setViewingGuideline(null);
  };

  const addTag = () => {
    if (tagInput.trim() && !newGuideline.tags.includes(tagInput.trim())) {
      setNewGuideline({ ...newGuideline, tags: [...newGuideline.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewGuideline({ ...newGuideline, tags: newGuideline.tags.filter(t => t !== tag) });
  };

  const filteredGuidelines = guidelines.filter(g => {
    const matchesSearch = !searchQuery || 
      g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || g.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const groupedGuidelines = filteredGuidelines.reduce((acc, g) => {
    if (!acc[g.category]) acc[g.category] = [];
    acc[g.category].push(g);
    return acc;
  }, {});

  // Calculate progress
  const totalGuidelines = guidelines.length;
  const completedCount = completedGuidelines.length;
  const overallProgress = totalGuidelines > 0 ? (completedCount / totalGuidelines) * 100 : 0;

  // Step-by-step viewer
  if (viewingGuideline) {
    return (
      <StepByStepViewer
        guideline={viewingGuideline}
        onClose={() => setViewingGuideline(null)}
        onComplete={() => handleCompleteGuideline(viewingGuideline.id)}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="guidelines-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Learning Center</h1>
          <p className="text-slate-500 mt-1">Step-by-step guides for all Enable Healthcare solutions</p>
        </div>
        {isAdmin && activeTab === 'guidelines' && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="create-guideline-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Guideline
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create Guideline</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateGuideline} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={newGuideline.title}
                    onChange={(e) => setNewGuideline({ ...newGuideline, title: e.target.value })}
                    placeholder="EHR Product Overview"
                    required
                    data-testid="guideline-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={newGuideline.category} 
                    onValueChange={(v) => setNewGuideline({ ...newGuideline, category: v })}
                  >
                    <SelectTrigger data-testid="guideline-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Content (Use ## headers to create steps) *</Label>
                  <Textarea
                    value={newGuideline.content}
                    onChange={(e) => setNewGuideline({ ...newGuideline, content: e.target.value })}
                    placeholder="## Step 1: Introduction&#10;&#10;Content for step 1...&#10;&#10;## Step 2: Setup&#10;&#10;Content for step 2..."
                    className="min-h-[200px] font-mono text-sm"
                    required
                    data-testid="guideline-content"
                  />
                  <p className="text-xs text-gray-500">Tip: Use ## headers to automatically create steps in the learning flow</p>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add tag"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newGuideline.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="submit-guideline-btn">
                    Create Guideline
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit" data-testid="guidelines-tabs">
        <button
          onClick={() => setActiveTab('learning-paths')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'learning-paths'
              ? 'bg-white text-[#1B365D] shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-learning-paths"
        >
          <Rocket className="w-4 h-4" />
          Learning Paths
        </button>
        <button
          onClick={() => setActiveTab('guidelines')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'guidelines'
              ? 'bg-white text-[#1B365D] shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-guidelines"
        >
          <BookOpen className="w-4 h-4" />
          Guidelines
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'learning-paths' ? (
        <LearningPaths />
      ) : (
      <>
      {/* Progress Overview */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-[#1B365D] to-[#0071E3] text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold mb-1">Your Learning Progress</h2>
              <p className="text-white/70 text-sm">
                {completedCount} of {totalGuidelines} guidelines completed
              </p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold">{Math.round(overallProgress)}%</span>
            </div>
          </div>
          <Progress value={overallProgress} className="h-2 mt-4 bg-white/20" />
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search guidelines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-full"
            data-testid="search-guidelines"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 rounded-full" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading guidelines...</div>
        </div>
      ) : filteredGuidelines.length > 0 ? (
        <div className="space-y-8">
          {Object.entries(groupedGuidelines).map(([category, items]) => {
            const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG['Sales'];
            const CategoryIcon = config.icon;
            const categoryCompleted = items.filter(g => completedGuidelines.includes(g.id)).length;
            const isLearningPath = items.some(g => g.is_learning_path);
            
            // Sort learning path items by order
            const sortedItems = isLearningPath 
              ? [...items].sort((a, b) => (a.order || 0) - (b.order || 0))
              : items;
            
            return (
              <div key={category}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${config.color}`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-[#1B365D]">{category}</h2>
                      <p className="text-sm text-gray-500">{config.description}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {categoryCompleted}/{items.length} completed
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sortedItems.map((guideline, idx) => {
                    const isCompleted = completedGuidelines.includes(guideline.id);
                    const moduleNum = guideline.is_learning_path ? guideline.order : null;
                    
                    return (
                      <motion.div
                        key={guideline.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <Card 
                          className={`border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group ${
                            isCompleted ? 'bg-emerald-50 border-emerald-200' : ''
                          }`}
                          onClick={() => setViewingGuideline(guideline)}
                          data-testid={`guideline-${guideline.id}`}
                        >
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  {isCompleted ? (
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                  ) : moduleNum ? (
                                    <span className="w-5 h-5 rounded-full bg-[#1B365D] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{moduleNum}</span>
                                  ) : (
                                    <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                                  )}
                                  <h3 className="font-semibold text-slate-900 truncate">{guideline.title}</h3>
                                </div>
                                <p className="text-sm text-slate-500 line-clamp-2 ml-7">
                                  {guideline.content.slice(0, 100).replace(/[#*_]/g, '')}...
                                </p>
                              </div>
                              <div className={`p-2 rounded-full transition-colors ${
                                isCompleted 
                                  ? 'bg-emerald-100 text-emerald-600' 
                                  : 'bg-gray-100 text-gray-400 group-hover:bg-[#1B365D] group-hover:text-white'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle2 className="w-4 h-4" />
                                ) : (
                                  <Play className="w-4 h-4" />
                                )}
                              </div>
                            </div>
                            
                            {guideline.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-3 ml-7">
                                {guideline.tags.slice(0, 3).map(tag => (
                                  <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                                ))}
                              </div>
                            )}
                            
                            {isAdmin && (
                              <div className="mt-3 ml-7">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteGuideline(guideline.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-apple">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">No guidelines found</h3>
            <p className="text-slate-500">
              {searchQuery || categoryFilter !== 'all' 
                ? 'Try adjusting your search or filter'
                : 'Guidelines will appear here once added.'}
            </p>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
};

export default Guidelines;
